import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Avatar,
  Flex,
  Spinner,
  useColorModeValue,
  useToast,
  Badge,
  Divider,
  useBreakpointValue,
} from '@chakra-ui/react';
import {TablerModalCloseButton as ModalCloseButton} from './TablerCloseButtons';
import {
  TablerSearch,
  TablerCalendar,
  TablerChevronRight,
  TablerUserX,
  TablerTrash,
  TablerX,
} from '../../icons';
import {useUser} from '../../contexts/UserContext';
import {patientService} from '../../services/patientService';
import useEhrPatientSearch from './useEhrPatientSearch';
import useUnifiedPatients from './useUnifiedPatients';
import {isMockEhrEnabled} from '../../mocks/mockEhrFixtures';
import EhrDeepSearchStatus from './EhrDeepSearchStatus';
import EhrDeepSearchBanner from './EhrDeepSearchBanner';
import useAppointmentStatuses from './useAppointmentStatuses';
import useRecentPatients from './useRecentPatients';
import useTodayPatients from './useTodayPatients';
import PatientPickerSections from './PatientPickerSections';
import usePlannerStyles from '../planner/usePlannerStyles';
import {
  PatientSection,
  CREATE_SESSION_CARD_PADDING,
} from '../planner/CreateSessionFormSections';
import {CREATE_SESSION_SECTION_LABEL_PROPS} from '../planner/createSessionSectionLayout';
import useCreateSessionPatients from '../planner/useCreateSessionPatients';
import {patientDisplayName as createSessionPatientDisplayName} from '../planner/createSessionFormUtils';
import {formatFacilityTime} from '../../utils/appointmentStatus';
import {patientMatchesSearch} from '../../utils/patientSearch';
import {
  RECAP_BUTTON_HEIGHT,
  RECAP_PRIMARY_BUTTON_PROPS,
} from './recapButtonStyles';
import {
  OPALITE_BLUE,
  initials,
  displayName,
  avatarFlatColor,
  isNonEnglish,
  languageFlag,
  formatDobShort,
  computeAge,
} from '../../constants/designTokens';

// ─────────────────────────────────────────────────────────────────────────────
// ChangeSessionPatientModal
//
// One reusable modal for "I picked the wrong chart on this session". Mirrors
// the shared patient-picker surface (PatientPickerSections) so providers see
// the same affordances (Today's appointments, Recent patients, search, deep
// EHR search) in both flows — the only difference here is that selecting a
// patient writes the change to an existing Conversation instead of starting
// a new one.
//
// EHR-enabled accounts are the primary motivation: a typo or a near-name
// collision can silently route a scribe note into a different patient's chart
// in the EHR. Reassigning here clears the cached encounter pointer so the
// next push reroutes correctly. Non-EHR accounts get the same modal with the
// EHR sections invisible (and a smaller list of local patients).
//
// Props:
//   isOpen        Controlled open state.
//   onClose       Called on close (Cancel / X / after a successful save).
//   session       The conversation being edited. Used to surface the current
//                 patient and the session label in the header.
//   onChanged     (updatedSession) => void — fired after a successful save
//                 with the merged session object so callers can update local
//                 state without an extra refetch.
//
// Save semantics:
//   - By default, selecting a patient row immediately attempts the
//     reassignment via `patientService.reassignSessionPatient` (backend
//     route). With `deferSubmit`, selecting marks a row and the footer Submit
//     button performs the reassignment. Errors surface as a toast and the
//     modal stays open so the user can retry / pick a different patient.
//   - The "Remove patient from session" button (visible only when the
//     session currently has a patient) clears all three identifiers in a
//     single call. We require an explicit confirm() since this is the only
//     destructive action in the modal.
// ─────────────────────────────────────────────────────────────────────────────

// Fixed visual footprint of the picker so the modal body doesn't grow
// when async data lands. Reserving the height upfront means the spinner
// state, the "today/recent" sections state, and the search-results
// state all occupy the same vertical real estate — patient rows never
// shift under the user's cursor between loading phases. On mobile we
// stretch the picker to fill the available viewport (the modal itself
// is full-screen there) so the list reads as a native sheet, not a
// fixed-height popover floating in dead space.
const PICKER_AREA_MIN_HEIGHT_DESKTOP = '420px';
const PICKER_AREA_MIN_HEIGHT_MOBILE = 'calc(100vh - 360px)';
const PATIENT_EDIT_CARD_PADDING = {base: 4, md: 5};
const PATIENT_EDIT_CLOSE_BUTTON_SIZE = '1.5rem';
const PATIENT_EDIT_CLOSE_BUTTON_TOP = {
  base: 'calc(1rem + 0.4375rem)',
  md: 'calc(1.25rem + 0.4375rem)',
};
const PATIENT_EDIT_CLOSE_BUTTON_X_ALIGN_OFFSET = '0.25rem';

const getUnifiedPatientName = (item: any) => {
  const localPatient = item?.localPatient || {};
  const ehrPatient = item?.ehrPatient || {};
  return `${localPatient.first_name || ehrPatient.firstName || ''} ${localPatient.last_name || ehrPatient.lastName || ''}`.trim();
};

const getUnifiedPatientEhrId = (item: any) => {
  const ehrPatient = item?.ehrPatient || {};
  const direct =
    ehrPatient.ehrSourceId || ehrPatient.ehr_source_id || ehrPatient.id;
  if (direct) return String(direct).split('/').pop() || null;
  // A local patient's `fhir_patient_id` link is deliberately NOT used here:
  // local and EHR patients stay distinct, so reassigning to a local-only
  // patient must not resolve an EHR id from the link.
  return null;
};

const splitPatientName = (value: any) => {
  const parts = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length <= 1) {
    return {firstName: parts[0] || '', lastName: ''};
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const sessionToPatientItem = (session: any) => {
  if (
    !session ||
    (!session.patient_name && !session.patient_id && !session.ehr_patient_id)
  ) {
    return null;
  }
  const {firstName, lastName} = splitPatientName(session.patient_name);
  const language =
    session.foreignLanguage || session.foreign_language || session.language;
  const localPatient = session.patient_id
    ? {
        id: session.patient_id,
        first_name: firstName,
        last_name: lastName,
        preferred_language: language || null,
      }
    : null;
  const ehrPatient = session.ehr_patient_id
    ? {
        id: session.ehr_patient_id,
        ehrSourceId: session.ehr_patient_id,
        firstName,
        lastName,
        language: language || null,
      }
    : null;
  return {
    id: localPatient?.id
      ? `local_${localPatient.id}`
      : `ehr_${session.ehr_patient_id || session.patient_name || 'patient'}`,
    localPatient,
    ehrPatient,
    matchKind: localPatient && ehrPatient ? 'matched' : 'sessionPatient',
  };
};

const ChangeSessionPatientModal = ({
  isOpen,
  onClose,
  session,
  onChanged,
  // Optional persistence override. When provided, the modal calls this with
  // the `{ patientId, ehrPatientId, patientName }` payload instead of the
  // Conversation-only `reassignSessionPatient` backend route — letting other
  // surfaces (e.g. a scheduled RecallBot) reuse the exact same picker while
  // writing the change wherever they own it. May return a merged object.
  onReassign = null,
  // Optional header copy overrides so the same picker reads naturally on
  // surfaces that aren't a recorded "session" (e.g. a scheduled visit).
  title = null,
  subtitle = null,
  // Optional two-step mode: selecting a row marks it, and the footer submit
  // button performs the reassignment. Existing callers keep the immediate
  // row-submit behavior by default.
  deferSubmit = false,
  submitLabel = 'Submit',
  profileStyle = false,
  entityLabel = 'session',
  profileShowInfoSection = true,
  profileActionsLabel = 'Actions',
  profilePatientTitle = 'Reassign Encounter',
  profilePatientDescription = `Change ownership of this ${entityLabel || 'session'} to another patient`,
  profileSubmitJustify = 'flex-start',
  onDeleteEncounter = null,
}: Record<string, any>) => {
  const {userData} = useUser();
  const toast = useToast();
  const profileStyles = usePlannerStyles();
  const normalizedEntityLabel = entityLabel || 'session';
  // Phone-class viewport detection. Drives a handful of layout decisions
  // (full-screen modal, taller patient rows, full-width cancel, taller
  // picker viewport). We resolve once at render time rather than
  // sprinkling `{ base, md }` everywhere because several of those
  // choices feed into JSX structure, not just style props.
  const isMobile = useBreakpointValue({base: true, md: false});
  const pickerMinH = isMobile
    ? PICKER_AREA_MIN_HEIGHT_MOBILE
    : PICKER_AREA_MIN_HEIGHT_DESKTOP;

  const [patientSearch, setPatientSearch] = useState('');
  const [pendingPatientItem, setPendingPatientItem] = useState<any>(null);
  const [selectedProfilePatientItem, setSelectedProfilePatientItem] =
    useState<any>(null);
  const [isPatientTypeaheadOpen, setIsPatientTypeaheadOpen] = useState(false);
  const [shouldFocusPatientSearch, setShouldFocusPatientSearch] =
    useState(false);
  const [deletingEncounter, setDeletingEncounter] = useState(false);
  const patientTypeaheadRef = useRef<any>(null);
  const {
    filteredPatients: createSessionFilteredPatients,
    isLoadingPatients: isLoadingCreateSessionPatients,
    isSearchingEhrPatients: isSearchingCreateSessionEhrPatients,
  } = useCreateSessionPatients({userData, patientSearch});

  // Initial load — patients (for local roster + non-EHR companies) and EHR
  // config (to decide whether the picker should expose the EHR sections /
  // deep search / roster hint).
  const {
    hasActiveEhr,
    ehrConfig,
    rosterCount,
    isLoading: loadingPatients,
    error: patientsError,
    buildUnifiedPatients,
  } = useUnifiedPatients({
    userData,
    enabled: isOpen,
    withRosterCount: true,
  });
  const networkEhrActive = hasActiveEhr && !isMockEhrEnabled(userData);

  const [savingPatientId, setSavingPatientId] = useState<any>(null);
  const [clearing, setClearing] = useState(false);

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const currentPanelBg = useColorModeValue('gray.50', 'gray.900');
  const currentPanelBorder = useColorModeValue('gray.100', 'gray.700');

  const {
    ehrPatients,
    isSearching: isSearchingEhrPatients,
    isDeepSearching,
    deepSearchSlow,
    deepSearchUnavailable,
    deepSearchError,
    deepSearchCompletion,
    canTriggerManualDeepSearch,
    triggerDeepSearch,
  } = useEhrPatientSearch({
    companyId: userData?.companyId,
    hasActiveEhr: networkEhrActive,
    searchQuery: patientSearch,
    cachedPatients: [],
  });

  const {
    statusByPatientId: appointmentStatusByPatientId,
    timeZone: facilityTimeZone,
  } = useAppointmentStatuses({
    companyId: userData?.companyId,
    hasActiveEhr: networkEhrActive,
    ehrConfig,
    provider: {displayName: userData?.displayName, email: userData?.email},
  });

  // Hydrate EHR patient docs for today's appointment ids so the "Today"
  // section renders complete rows (mirrors the other picker surfaces —
  // without this hook, today's appointment rows pointing at patients
  // outside the legacy slab would render as missing entries).
  const {todayPatients} = useTodayPatients({
    companyId: userData?.companyId,
    hasActiveEhr: networkEhrActive,
    statusByPatientId: appointmentStatusByPatientId,
  });

  const {recentPatients, loading: loadingRecents} = useRecentPatients({
    userId: userData?.userId,
    companyId: userData?.companyId,
    enabled: Boolean(networkEhrActive && isOpen),
  });

  const currentSessionPatientItem = useMemo(
    () => sessionToPatientItem(session),
    [session],
  );

  // Reset the search box each time the modal opens; the roster itself
  // loads through `useUnifiedPatients` (keyed on `isOpen`).
  useEffect(() => {
    if (isOpen) {
      setPatientSearch('');
      setPendingPatientItem(null);
      setSelectedProfilePatientItem(currentSessionPatientItem);
      setIsPatientTypeaheadOpen(false);
      setShouldFocusPatientSearch(false);
    }
  }, [isOpen, currentSessionPatientItem]);

  useEffect(() => {
    if (!profileStyle) return undefined;
    const handlePointerDown = (event: any) => {
      const target = event.target;
      const patientDropdown = document.getElementById(
        'create-session-patient-results',
      );
      if (
        patientTypeaheadRef.current &&
        !patientTypeaheadRef.current.contains(target) &&
        !patientDropdown?.contains(target)
      ) {
        setIsPatientTypeaheadOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [profileStyle]);

  useEffect(() => {
    if (isOpen && patientsError) {
      toast({title: 'Error loading patients', status: 'error', duration: 3000});
    }
  }, [isOpen, patientsError, toast]);

  // Unified list combines local roster with EHR search results (when the
  // user is searching). The "default" view (no query) renders via
  // PatientPickerSections instead, which uses its own Today/Recent source.
  const unifiedPatients = useMemo(
    () => buildUnifiedPatients(ehrPatients),
    [buildUnifiedPatients, ehrPatients],
  );

  const getEhrPatientIdForItem = useCallback((item: any) => {
    const ehrId = item?.ehrPatient?.ehrSourceId || item?.ehrPatient?.id;
    if (ehrId) return String(ehrId);
    const linkedId = item?.localPatient?.fhir_patient_id;
    if (linkedId) return String(linkedId).split('/').pop();
    return null;
  }, []);

  const filteredPatients = useMemo(() => {
    if (!patientSearch.trim()) {
      if (hasActiveEhr) return [];
      return [...unifiedPatients].slice(0, 30);
    }
    return unifiedPatients
      .filter((item: any) => {
        const p = item.localPatient || {};
        const e = item.ehrPatient || {};
        return patientMatchesSearch(patientSearch, {
          first_name: p.first_name || e.firstName,
          middleName: e.middleName,
          last_name: p.last_name || e.lastName,
          mrn: p.mrn || e.mrn,
          accountNo: e.accountNo,
          preferred_language: p.preferred_language || e.language,
          dob: e.dob,
          date_of_birth: p.date_of_birth,
          id: p.id || e.id,
          ehrSourceId: e.ehrSourceId,
        });
      })
      .slice(0, 50);
  }, [unifiedPatients, patientSearch, hasActiveEhr]);

  const sectionsUnifiedSource = useMemo(
    () => buildUnifiedPatients(todayPatients.map(t => t.ehrPatient)),
    [buildUnifiedPatients, todayPatients],
  );

  // Resolve a unified item to the shape the backend reassignment route
  // expects. Local-only patients send `patientId` and skip the EHR id;
  // EHR-only patients send `ehrPatientId` and skip the local id; merged
  // rows send both so future reads work via either path.
  const buildReassignmentPayload = useCallback((item: any) => {
    const localId = item?.localPatient?.id || null;
    const ehrId = getUnifiedPatientEhrId(item);
    const patientName = getUnifiedPatientName(item);
    return {
      patientId: localId,
      ehrPatientId: ehrId,
      patientName,
    };
  }, []);

  const isSameAsCurrent = useCallback(
    (item: any) => {
      if (!session) return false;
      const payload = buildReassignmentPayload(item);
      const currentPatientId = session.patient_id || null;
      const currentEhrId = session.ehr_patient_id || null;
      return (
        payload.patientId === currentPatientId &&
        payload.ehrPatientId === currentEhrId
      );
    },
    [session, buildReassignmentPayload],
  );

  const persistPatientSelection = useCallback(
    async (item: any) => {
      if (!session?.id) return;
      const payload = buildReassignmentPayload(item);
      setSavingPatientId(item.id);
      try {
        const updated = onReassign
          ? await onReassign(payload)
          : await patientService.reassignSessionPatient(session.id, payload);
        const mergedSession = {
          ...session,
          patient_id: payload.patientId,
          ehr_patient_id: payload.ehrPatientId,
          patient_name: payload.patientName,
          ...(updated || {}),
        };
        onChanged?.(mergedSession, {patientItem: item, payload});
        toast({
          title: 'Patient updated',
          description: payload.patientName
            ? `${normalizedEntityLabel[0].toUpperCase()}${normalizedEntityLabel.slice(1)} reassigned to ${payload.patientName}.`
            : `Patient updated on ${normalizedEntityLabel}.`,
          status: 'success',
          duration: 2800,
        });
        onClose?.();
      } catch (error) {
        toast({
          title: 'Could not reassign patient',
          description: error?.message || 'Please try again.',
          status: 'error',
          duration: 3500,
        });
      } finally {
        setSavingPatientId(null);
      }
    },
    [
      session,
      onClose,
      onChanged,
      toast,
      buildReassignmentPayload,
      onReassign,
      normalizedEntityLabel,
    ],
  );

  const handleSelectPatient = useCallback(
    async (item: any) => {
      if (!session?.id) return;
      if (isSameAsCurrent(item)) {
        toast({
          title: 'Already assigned',
          description: `This ${normalizedEntityLabel} is already attached to that patient.`,
          status: 'info',
          duration: 2500,
        });
        return;
      }
      if (deferSubmit) {
        setPendingPatientItem(item);
        return;
      }
      await persistPatientSelection(item);
    },
    [
      session,
      isSameAsCurrent,
      deferSubmit,
      persistPatientSelection,
      toast,
      normalizedEntityLabel,
    ],
  );

  const handleSubmitPendingPatient = useCallback(async () => {
    if (!pendingPatientItem) return;
    await persistPatientSelection(pendingPatientItem);
  }, [pendingPatientItem, persistPatientSelection]);

  const handleProfileSelectPatient = useCallback(
    (item: any) => {
      setSelectedProfilePatientItem(item);
      setPatientSearch('');
      setIsPatientTypeaheadOpen(false);
      setShouldFocusPatientSearch(false);
      setPendingPatientItem(isSameAsCurrent(item) ? null : item);
    },
    [isSameAsCurrent],
  );

  const handleProfileClearPatient = useCallback(() => {
    setSelectedProfilePatientItem(null);
    setPendingPatientItem(null);
    setPatientSearch('');
    setIsPatientTypeaheadOpen(true);
    setShouldFocusPatientSearch(true);
  }, []);

  const handleProfileEditPatient = useCallback(() => {
    setSelectedProfilePatientItem(null);
    setPendingPatientItem(null);
    setPatientSearch('');
    setIsPatientTypeaheadOpen(true);
    setShouldFocusPatientSearch(true);
  }, []);

  const handleProfilePatientSearchChange = useCallback((event: any) => {
    setPatientSearch(event.target.value);
    setIsPatientTypeaheadOpen(true);
  }, []);

  const handleClearPatient = useCallback(async () => {
    if (!session?.id) return;
    const ok =
      typeof window !== 'undefined'
        ? window.confirm(
            `Remove the patient from this ${normalizedEntityLabel}? It will no longer appear on any patient's history.`,
          )
        : true;
    if (!ok) return;
    setClearing(true);
    try {
      const clearPayload = {
        patientId: null,
        ehrPatientId: null,
        patientName: '',
      };
      const updated = onReassign
        ? await onReassign(clearPayload)
        : await patientService.reassignSessionPatient(session.id, clearPayload);
      const mergedSession = {
        ...session,
        patient_id: null,
        ehr_patient_id: null,
        patient_name: '',
        ...(updated || {}),
      };
      onChanged?.(mergedSession, {patientItem: null, payload: clearPayload});
      toast({
        title: `Patient removed from ${normalizedEntityLabel}`,
        status: 'success',
        duration: 2500,
      });
      onClose?.();
    } catch (error) {
      toast({
        title: 'Could not remove patient',
        description: error?.message || 'Please try again.',
        status: 'error',
        duration: 3500,
      });
    } finally {
      setClearing(false);
    }
  }, [session, onClose, onChanged, toast, onReassign, normalizedEntityLabel]);

  const handleDeleteEncounter = useCallback(async () => {
    if (!onDeleteEncounter) return;
    const ok =
      typeof window !== 'undefined'
        ? window.confirm(
            'Delete this encounter? It will be deleted from patient history.',
          )
        : true;
    if (!ok) return;
    setDeletingEncounter(true);
    try {
      await onDeleteEncounter();
      toast({
        title: 'Encounter deleted',
        status: 'success',
        duration: 2500,
      });
      onClose?.();
    } catch (error) {
      toast({
        title: 'Could not delete encounter',
        description: error?.message || 'Please try again.',
        status: 'error',
        duration: 3500,
      });
    } finally {
      setDeletingEncounter(false);
    }
  }, [onDeleteEncounter, onClose, toast]);

  // Single row renderer used by both the default sections and the search
  // results list. We namespace avatar colors by section so today/recent/
  // search rows don't all start at index 0.
  const renderPatientRow = useCallback(
    ({item, sectionKey, indexInSection, apptStatus}: Record<string, any>) => {
      const p = item.localPatient || {};
      const e = item.ehrPatient || {};
      const pDisplay = {
        first_name: p.first_name || e.firstName || '',
        last_name: p.last_name || e.lastName || '',
        preferred_language: p.preferred_language || e.language || null,
        preferred_dialect: p.preferred_dialect || e.dialect || null,
        mrn: p.mrn || e.mrn || null,
        date_of_birth: p.date_of_birth || e.dob || null,
      };
      const apptIsNow = apptStatus?.kind === 'now';
      const apptIsSoon = apptStatus?.kind === 'soon';
      const colorIdx =
        (sectionKey === 'today' ? 0 : sectionKey === 'recent' ? 2 : 4) +
        indexInSection;
      const isCurrent = isSameAsCurrent(item);
      const isPending = deferSubmit && pendingPatientItem?.id === item.id;
      const isSaving = savingPatientId === item.id;

      return (
        <Flex
          align="center"
          px={{base: 3.5, md: 4}}
          py={{base: 3.5, md: 3}}
          mb={2}
          cursor={isCurrent ? 'default' : 'pointer'}
          transition="background 0.12s, border-color 0.12s"
          _hover={
            !isCurrent ? {bg: hoverBg, borderColor: 'gray.300'} : undefined
          }
          _active={
            !isCurrent ? {bg: hoverBg, transform: 'scale(0.99)'} : undefined
          }
          opacity={isCurrent ? 0.65 : 1}
          onClick={() => !isCurrent && handleSelectPatient(item)}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={
            isPending
              ? 'opalite.500'
              : apptIsNow || apptIsSoon
                ? 'transparent'
                : borderColor
          }
          // 60px mobile / 52px desktop. iOS HIG and Material both recommend
          // ≥44pt for primary touch targets; 60px gives breathing room
          // around the avatar + two-line text without feeling cramped on
          // small screens.
          minH={{base: '60px', md: '52px'}}
          bg={
            isPending
              ? 'opalite.50'
              : apptIsNow
                ? 'gray.50'
                : apptIsSoon
                  ? 'gray.50'
                  : undefined
          }
          borderLeftWidth="1px"
          borderLeftColor={apptIsNow || apptIsSoon ? 'gray' : borderColor}
          role="button"
          tabIndex={isCurrent ? -1 : 0}
          onKeyDown={ev =>
            !isCurrent && ev.key === 'Enter' && handleSelectPatient(item)
          }
        >
          <Avatar
            name={initials(pDisplay.first_name, pDisplay.last_name)}
            bg={avatarFlatColor(colorIdx)}
            color="white"
            size={{base: 'md', md: 'sm'}}
            mr={3}
            flexShrink={0}
          />
          <Box flex={1} minW={0}>
            <HStack spacing={2} flexWrap="wrap">
              <Text
                fontWeight="normal"
                fontSize="md"
                noOfLines={1}
                color="gray.900"
              >
                {displayName(pDisplay.first_name, pDisplay.last_name)}
              </Text>
              {getUnifiedPatientEhrId(item) && (
                <Badge colorScheme="gray" fontSize="xs" variant="subtle">
                  EHR
                </Badge>
              )}
              {isCurrent && (
                <Badge colorScheme="gray" fontSize="xs" variant="subtle">
                  Current
                </Badge>
              )}
              {isPending && (
                <Badge colorScheme="opalite" fontSize="xs" variant="subtle">
                  Selected
                </Badge>
              )}
              {apptStatus &&
                (apptIsNow ||
                  apptIsSoon ||
                  apptStatus.kind === 'laterToday' ||
                  apptStatus.kind === 'justEnded') && (
                  <Text
                    fontSize="xs"
                    fontWeight="normal"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    color="white"
                    bg={
                      apptIsNow
                        ? '#919191'
                        : apptIsSoon
                          ? '#767676'
                          : apptStatus.kind === 'justEnded'
                            ? '#9B9B9B'
                            : '#D4D4D4'
                    }
                  >
                    {apptIsNow
                      ? `Now \u00B7 ${formatFacilityTime(apptStatus.start, facilityTimeZone)}`
                      : apptIsSoon
                        ? `In ${Math.max(1, apptStatus.minutesUntilStart ?? 0)}m`
                        : apptStatus.kind === 'justEnded'
                          ? 'Just ended'
                          : formatFacilityTime(
                              apptStatus.start,
                              facilityTimeZone,
                            )}
                  </Text>
                )}
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              {isNonEnglish(pDisplay.preferred_language) && (
                <HStack spacing={1}>
                  <Text fontSize="sm">
                    {languageFlag(pDisplay.preferred_language)}
                  </Text>
                  <Text fontSize="sm" color={subtleText}>
                    {pDisplay.preferred_language}
                  </Text>
                </HStack>
              )}
              {pDisplay.mrn && (
                <Text fontSize="sm" color={subtleText}>
                  MRN: {pDisplay.mrn}
                </Text>
              )}
              {pDisplay.date_of_birth && (
                <HStack spacing={1}>
                  <TablerCalendar size={12} color="#ACACAC" />
                  <Text fontSize="sm" color={subtleText}>
                    {formatDobShort(pDisplay.date_of_birth)}
                    {computeAge(pDisplay.date_of_birth) != null &&
                      ` (${computeAge(pDisplay.date_of_birth)})`}
                  </Text>
                </HStack>
              )}
            </HStack>
          </Box>
          {isSaving ? (
            <Spinner size="sm" color="opalite.500" />
          ) : (
            <TablerChevronRight size={16} color="#ACACAC" />
          )}
        </Flex>
      );
    },
    [
      hoverBg,
      borderColor,
      subtleText,
      facilityTimeZone,
      handleSelectPatient,
      savingPatientId,
      isSameAsCurrent,
      deferSubmit,
      pendingPatientItem?.id,
    ],
  );

  const currentPatientName = (session?.patient_name || '').trim();
  const hasCurrentPatient = Boolean(
    currentPatientName || session?.patient_id || session?.ehr_patient_id,
  );
  const sessionId = session?.id || session?.conversationId || 'Unavailable';
  const selectedProfilePatientName = createSessionPatientDisplayName(
    selectedProfilePatientItem,
  );
  const shouldShowProfilePatientResults =
    isPatientTypeaheadOpen && !selectedProfilePatientItem;
  const profileActionsHeadingId = profileActionsLabel
    ? 'edit-encounter-actions-heading'
    : undefined;

  if (profileStyle) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          data-testid="edit-encounter-modal"
          bg="transparent"
          boxShadow="none"
          w="calc(100% - 2rem)"
          maxW="750px"
          mx={4}
        >
          <IconButton
            aria-label="Close"
            icon={<TablerX size={16} />}
            position="absolute"
            top={PATIENT_EDIT_CLOSE_BUTTON_TOP}
            right={CREATE_SESSION_CARD_PADDING}
            transform={`translateX(${PATIENT_EDIT_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
            zIndex={1}
            boxSize={PATIENT_EDIT_CLOSE_BUTTON_SIZE}
            minW={PATIENT_EDIT_CLOSE_BUTTON_SIZE}
            p={0}
            bg="transparent"
            color={profileStyles.subtleText}
            borderWidth="0"
            borderRadius="0"
            variant="ghost"
            onClick={onClose}
            _hover={{bg: 'transparent', color: profileStyles.textColor}}
            _active={{bg: 'transparent'}}
            _focus={{bg: 'transparent'}}
            _focusVisible={profileStyles.focusOutlineStyles}
          />
          <ModalBody p={0}>
            <Box
              bg={profileStyles.surfaceBg}
              borderWidth="1px"
              borderColor={profileStyles.borderColor}
              borderRadius="xl"
              boxShadow={profileStyles.cardShadow}
              overflow="visible"
            >
              <VStack
                spacing={4}
                align="stretch"
                p={CREATE_SESSION_CARD_PADDING}
              >
                <Text
                  as="h2"
                  color={profileStyles.textColor}
                  fontSize="2xl"
                  fontWeight="semibold"
                  lineHeight="1.25"
                  letterSpacing="0"
                  mb={0}
                  pt={1}
                  pb={2}
                  pr={10}
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {title || 'Edit Encounter'}
                </Text>

                {profileShowInfoSection ? (
                  <Box
                    as="section"
                    aria-labelledby="edit-encounter-info-heading"
                  >
                    <Flex
                      gap={{base: 2, md: 3}}
                      align={{base: 'stretch', md: 'flex-start'}}
                      direction={{base: 'column', md: 'row'}}
                    >
                      <Text
                        as="h3"
                        id="edit-encounter-info-heading"
                        color={profileStyles.textColor}
                        {...CREATE_SESSION_SECTION_LABEL_PROPS}
                        alignItems={{md: 'flex-start'}}
                        minH="auto"
                        pt={2}
                      >
                        Info
                      </Text>
                      <Box flex="1" minW={0} py={2}>
                        <Text
                          color={profileStyles.subtleText}
                          fontSize="sm"
                          fontWeight="normal"
                        >
                          Session ID
                        </Text>
                        <Text
                          color={profileStyles.textColor}
                          fontSize="md"
                          lineHeight="1.35"
                          wordBreak="break-word"
                        >
                          {sessionId}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ) : null}

                <Box
                  as="section"
                  aria-labelledby={profileActionsHeadingId}
                  borderTopWidth={profileShowInfoSection ? '1px' : 0}
                  borderColor={
                    profileStyles.listRowDividerColor ||
                    profileStyles.rowBorderColor
                  }
                  pt={profileShowInfoSection ? 4 : 0}
                >
                  <Flex
                    gap={{base: 2, md: 3}}
                    align={{base: 'stretch', md: 'flex-start'}}
                    direction={{base: 'column', md: 'row'}}
                  >
                    {profileActionsLabel ? (
                      <Text
                        as="h3"
                        id={profileActionsHeadingId}
                        color={profileStyles.textColor}
                        {...CREATE_SESSION_SECTION_LABEL_PROPS}
                        alignItems={{md: 'flex-start'}}
                        minH="auto"
                        pt={0}
                      >
                        {profileActionsLabel}
                      </Text>
                    ) : null}
                    <VStack flex="1" minW={0} align="stretch" spacing={3}>
                      {profilePatientTitle || profilePatientDescription ? (
                        <Box minW={0} w="100%">
                          {profilePatientTitle ? (
                            <Text
                              color={profileStyles.textColor}
                              fontSize="md"
                              lineHeight="1.35"
                            >
                              {profilePatientTitle}
                            </Text>
                          ) : null}
                          {profilePatientDescription ? (
                            <Text
                              color={profileStyles.subtleText}
                              fontSize="sm"
                              lineHeight="1.45"
                            >
                              {profilePatientDescription}
                            </Text>
                          ) : null}
                        </Box>
                      ) : null}
                      <PatientSection
                        autoFocusPatientSearch={shouldFocusPatientSearch}
                        compact
                        filteredPatients={createSessionFilteredPatients}
                        handleClearPatient={handleProfileClearPatient}
                        handleEditPatient={handleProfileEditPatient}
                        handlePatientSearchChange={
                          handleProfilePatientSearchChange
                        }
                        handleSelectPatient={handleProfileSelectPatient}
                        isLoadingPatients={isLoadingCreateSessionPatients}
                        isSearchingEhrPatients={
                          isSearchingCreateSessionEhrPatients
                        }
                        onPatientSearchFocus={() =>
                          setIsPatientTypeaheadOpen(true)
                        }
                        patientSearch={patientSearch}
                        patientTypeaheadRef={patientTypeaheadRef}
                        selectedPatientItem={selectedProfilePatientItem}
                        selectedPatientName={selectedProfilePatientName}
                        shouldShowResults={shouldShowProfilePatientResults}
                        visibleErrors={{}}
                        headingLabel="Change Patient"
                        headingTextTransform="none"
                        headingLetterSpacing="0"
                        showHeading={false}
                        portalResults
                      />

                      <Flex
                        justify={profileSubmitJustify}
                        gap={2}
                        flexWrap="wrap"
                        borderBottomWidth={onDeleteEncounter ? '1px' : 0}
                        borderColor={
                          profileStyles.listRowDividerColor ||
                          profileStyles.rowBorderColor
                        }
                        pb={onDeleteEncounter ? 3 : 0}
                      >
                        <Button
                          {...RECAP_PRIMARY_BUTTON_PROPS}
                          onClick={handleSubmitPendingPatient}
                          isDisabled={!pendingPatientItem}
                          isLoading={Boolean(savingPatientId)}
                          loadingText="Saving"
                          fontSize="sm"
                          px={4}
                        >
                          {submitLabel}
                        </Button>
                      </Flex>

                      {onDeleteEncounter ? (
                        <Flex
                          align={{base: 'stretch', sm: 'center'}}
                          justify="space-between"
                          direction={{base: 'column', sm: 'row'}}
                          gap={3}
                          py={2}
                        >
                          <Box minW={0} flex="1">
                            <Text
                              color={profileStyles.textColor}
                              fontSize="md"
                              lineHeight="1.35"
                            >
                              Delete Encounter
                            </Text>
                            <Text
                              color={profileStyles.subtleText}
                              fontSize="sm"
                              lineHeight="1.45"
                            >
                              Delete this encounter from patient history
                            </Text>
                          </Box>
                          <Button
                            variant="outline"
                            color="red.600"
                            borderColor="red.300"
                            leftIcon={
                              <TablerTrash size={14} aria-hidden="true" />
                            }
                            onClick={handleDeleteEncounter}
                            isLoading={deletingEncounter}
                            loadingText="Deleting"
                            alignSelf={{base: 'stretch', sm: 'flex-start'}}
                            h={RECAP_BUTTON_HEIGHT}
                            minH={RECAP_BUTTON_HEIGHT}
                            borderRadius="lg"
                            fontWeight="medium"
                            fontSize="sm"
                            _hover={{bg: 'red.50', borderColor: 'red.400'}}
                            _active={{bg: 'red.100', borderColor: 'red.500'}}
                            _focusVisible={profileStyles.focusOutlineStyles}
                          >
                            Delete Encounter
                          </Button>
                        </Flex>
                      ) : null}
                    </VStack>
                  </Flex>
                </Box>
              </VStack>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // Full-screen sheet on phones, centered dialog on tablet+. The
      // `isCentered` prop is ignored when `size="full"`, so passing it
      // unconditionally is safe — Chakra picks the right behaviour
      // per breakpoint.
      size={{base: 'full', md: '2xl'}}
      isCentered
      scrollBehavior="inside"
      // Edge-to-edge on mobile reads as a native sheet, with no stray
      // background showing around the corners.
      motionPreset={isMobile ? 'slideInBottom' : 'scale'}
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
      <ModalContent
        bg={profileStyle ? profileStyles.surfaceBg : undefined}
        borderWidth={profileStyle ? '1px' : undefined}
        borderColor={profileStyle ? profileStyles.borderColor : undefined}
        boxShadow={profileStyle ? profileStyles.cardShadow : undefined}
        borderRadius={profileStyle ? 'xl' : {base: 0, md: '2xl'}}
        mx={profileStyle ? 4 : {base: 0, md: 4}}
        my={{base: 0, md: 'auto'}}
        w={profileStyle ? 'calc(100% - 2rem)' : undefined}
        maxW={profileStyle ? '750px' : undefined}
        maxH={{base: '100vh', md: '88vh'}}
        h={profileStyle ? undefined : {base: '100vh', md: 'auto'}}
        overflow="hidden"
      >
        {profileStyle ? (
          <IconButton
            aria-label="Close"
            icon={<TablerX size={16} />}
            position="absolute"
            top={PATIENT_EDIT_CLOSE_BUTTON_TOP}
            right={PATIENT_EDIT_CARD_PADDING}
            transform={`translateX(${PATIENT_EDIT_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
            zIndex={1}
            boxSize={PATIENT_EDIT_CLOSE_BUTTON_SIZE}
            minW={PATIENT_EDIT_CLOSE_BUTTON_SIZE}
            p={0}
            bg="transparent"
            color={profileStyles.subtleText}
            borderWidth="0"
            borderRadius="0"
            variant="ghost"
            onClick={onClose}
            _hover={{bg: 'transparent', color: profileStyles.textColor}}
            _active={{bg: 'transparent'}}
            _focus={{bg: 'transparent'}}
            _focusVisible={profileStyles.focusOutlineStyles}
          />
        ) : null}
        <ModalHeader
          fontWeight="normal"
          pb={2}
          pt={profileStyle ? {base: 4, md: 5} : {base: 4, md: 5}}
          px={profileStyle ? PATIENT_EDIT_CARD_PADDING : {base: 4, md: 6}}
          pr={profileStyle ? 10 : undefined}
        >
          <Text
            fontSize={profileStyle ? '2xl' : {base: 'lg', md: 'xl'}}
            fontWeight={profileStyle ? 'semibold' : 'normal'}
            lineHeight="1.25"
            color={profileStyle ? profileStyles.textColor : undefined}
            letterSpacing="0"
            noOfLines={2}
            wordBreak="break-word"
          >
            {title || 'Change patient on this session'}
          </Text>
          {!profileStyle ? (
            <Text
              fontSize={{base: 'sm', md: 'md'}}
              color={subtleText}
              fontWeight="normal"
              mt={1.5}
              lineHeight="1.55"
            >
              {subtitle ||
                (hasCurrentPatient
                  ? `Pick the correct chart below. Notes stay attached to this ${normalizedEntityLabel}.`
                  : `Attach a patient to this ${normalizedEntityLabel}, or keep it unlinked.`)}
            </Text>
          ) : null}
        </ModalHeader>
        {profileStyle ? null : (
          <ModalCloseButton
            size="lg"
            top={{base: 4, md: 5}}
            right={{base: 4, md: 6}}
          />
        )}

        <ModalBody
          pb={4}
          px={profileStyle ? PATIENT_EDIT_CARD_PADDING : {base: 4, md: 6}}
        >
          {profileStyle ? (
            <Box
              as="section"
              aria-labelledby="edit-patient-session-heading"
              mb={4}
            >
              <Flex
                gap={{base: 2, md: 3}}
                align={{base: 'stretch', md: 'flex-start'}}
                direction={{base: 'column', md: 'row'}}
              >
                <Text
                  as="h3"
                  id="edit-patient-session-heading"
                  color={profileStyles.textColor}
                  fontSize="sm"
                  fontWeight="semibold"
                  lineHeight="1.25"
                  minH="auto"
                  minW={{md: '8rem'}}
                  pt={2}
                >
                  Session
                </Text>
                <Box flex="1" minW={0} py={2}>
                  <Text
                    color={profileStyles.subtleText}
                    fontSize="sm"
                    fontWeight="normal"
                  >
                    Session ID
                  </Text>
                  <Text
                    color={profileStyles.textColor}
                    fontSize="md"
                    lineHeight="1.35"
                    wordBreak="break-word"
                  >
                    {sessionId}
                  </Text>
                </Box>
              </Flex>
            </Box>
          ) : null}

          {hasCurrentPatient && (
            <Box
              bg={profileStyle ? 'transparent' : currentPanelBg}
              borderRadius={profileStyle ? 0 : 'lg'}
              borderWidth={profileStyle ? 0 : '1px'}
              borderTopWidth={profileStyle ? '1px' : undefined}
              borderColor={
                profileStyle
                  ? profileStyles.listRowDividerColor ||
                    profileStyles.rowBorderColor
                  : currentPanelBorder
              }
              px={profileStyle ? 0 : 4}
              py={profileStyle ? 3 : 2.5}
              mb={4}
            >
              {/* Compact single-row layout: eyebrow label + patient
                  name + EHR pill all live on one line, with Remove
                  pulled to the far right. Optional EHR-reset notice
                  collapses to a single tight line below. Earlier
                  version was a four-row stack that ate ~140px of
                  vertical space; this one runs ~56px (single row) or
                  ~80px (with the notice) — same WCAG-compliant
                  typography (≥14px), much less wasted space. */}
              <Flex align="center" gap={3} flexWrap="wrap">
                <Flex align="baseline" gap={2.5} minW={0} flex={1}>
                  <Text
                    fontSize="sm"
                    fontWeight="normal"
                    color={profileStyle ? profileStyles.subtleText : 'gray.700'}
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    flexShrink={0}
                  >
                    Attached
                  </Text>
                  <Text
                    fontSize="md"
                    fontWeight="normal"
                    color={profileStyle ? profileStyles.textColor : 'gray.900'}
                    noOfLines={1}
                    minW={0}
                  >
                    {currentPatientName || 'Unnamed patient'}
                  </Text>
                  {session?.ehr_patient_id ? (
                    <Badge
                      colorScheme="gray"
                      fontSize="xs"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontWeight="normal"
                      flexShrink={0}
                    >
                      EHR
                    </Badge>
                  ) : null}
                </Flex>

                <Button
                  size="sm"
                  variant={profileStyle ? 'outline' : 'ghost'}
                  colorScheme="gray"
                  color="gray.600"
                  leftIcon={<TablerUserX size={14} />}
                  onClick={handleClearPatient}
                  isLoading={clearing}
                  loadingText="Removing..."
                  fontWeight="normal"
                  fontSize="sm"
                  borderRadius="md"
                  h="32px"
                  px={2.5}
                  _hover={{bg: 'gray.50', color: 'gray.700'}}
                  _active={{bg: 'gray.100'}}
                  flexShrink={0}
                >
                  Remove
                </Button>
              </Flex>

              {/* EHR encounter reset notice. JSX text doesn't parse
                  `\u` escapes so this is intentionally a JS string
                  expression. Body fontSize="sm" (14px) keeps WCAG
                  body-text minimums. lineHeight=1.4 keeps the panel
                  tight when the notice does appear. */}
              {hasActiveEhr && session?.ehr_patient_id ? (
                <Text fontSize="sm" color="gray.800" mt={1.5} lineHeight="1.4">
                  {'Reassigning resets the cached EHR encounter; you\u2019ll need to ' +
                    'pick a new encounter the next time a note is pushed.'}
                </Text>
              ) : null}
            </Box>
          )}

          <InputGroup
            size="lg"
            mb={hasActiveEhr && patientSearch.trim() ? 2 : 4}
          >
            <InputLeftElement pointerEvents="none">
              <TablerSearch color="#7E7E7E" />
            </InputLeftElement>
            <Input
              placeholder="Search patients by name, MRN, DOB..."
              value={patientSearch}
              onChange={e => setPatientSearch(e.target.value)}
              bg="gray.50"
              border="none"
              borderRadius="xl"
              fontSize="md"
              _focus={{boxShadow: `0 0 0 2px ${OPALITE_BLUE}40`, bg: 'white'}}
              _placeholder={{color: 'gray.500'}}
              autoFocus
            />
          </InputGroup>

          {hasActiveEhr && !!patientSearch.trim() && (
            <Box mb={3}>
              <EhrDeepSearchBanner
                isDeepSearching={isDeepSearching}
                deepSearchSlow={deepSearchSlow}
                deepSearchUnavailable={deepSearchUnavailable}
                deepSearchError={deepSearchError}
                deepSearchCompletion={deepSearchCompletion}
                canTriggerManualDeepSearch={canTriggerManualDeepSearch}
                onTriggerDeepSearch={triggerDeepSearch}
                showManualTriggerWhenIdle
                promoted={
                  filteredPatients.length === 0 && !isSearchingEhrPatients
                }
                suppressInFlightAndUnavailable={filteredPatients.length === 0}
              />
            </Box>
          )}

          {/* Stable-height picker viewport.
              All three rendering states (initial spinner, EHR
              "Today / Recent" sections, search results) live inside
              the same minHeight container so the modal doesn't
              grow/shrink as async data arrives. On mobile we stretch
              the picker to fill the remaining viewport so the modal
              reads as a native bottom-sheet list; on desktop it
              stays a tidy 420px popover viewport. */}
          <Box minH={pickerMinH} position="relative">
            {loadingPatients ? (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                aria-live="polite"
                aria-busy="true"
              >
                <Spinner color="opalite.500" size="lg" />
              </Flex>
            ) : hasActiveEhr && !patientSearch.trim() ? (
              <Box maxH={pickerMinH} overflowY="auto" pr={1}>
                {loadingRecents &&
                recentPatients.length === 0 &&
                todayPatients.length === 0 ? (
                  <Flex
                    minH={pickerMinH}
                    align="center"
                    justify="center"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <Spinner size="md" color="opalite.500" />
                  </Flex>
                ) : (
                  <PatientPickerSections
                    unifiedPatients={sectionsUnifiedSource}
                    recentPatients={recentPatients}
                    appointmentStatusByPatientId={appointmentStatusByPatientId}
                    getEhrPatientIdForItem={getEhrPatientIdForItem}
                    renderRow={renderPatientRow}
                    roster={
                      typeof rosterCount === 'number'
                        ? {count: rosterCount}
                        : undefined
                    }
                  />
                )}
              </Box>
            ) : isSearchingEhrPatients && filteredPatients.length === 0 ? (
              <Flex
                minH={pickerMinH}
                align="center"
                justify="center"
                aria-live="polite"
                aria-busy="true"
              >
                <Spinner color="opalite.500" size="lg" />
              </Flex>
            ) : (
              <VStack
                spacing={0}
                align="stretch"
                maxH={pickerMinH}
                overflowY="auto"
                overflowX="hidden"
                pr={1}
              >
                {filteredPatients.length === 0 ? (
                  <Flex justify="center" py={6} px={4}>
                    {patientSearch.trim() ? (
                      <EhrDeepSearchStatus
                        isDeepSearching={isDeepSearching}
                        deepSearchSlow={deepSearchSlow}
                        deepSearchUnavailable={deepSearchUnavailable}
                        canTriggerManualDeepSearch={false}
                        emptyStateText={`No patients match "${patientSearch.trim()}".`}
                      />
                    ) : (
                      <Text color={subtleText} fontSize="sm">
                        No patients found
                      </Text>
                    )}
                  </Flex>
                ) : (
                  filteredPatients.map((item: any, idx: any) => {
                    const apptStatus = appointmentStatusByPatientId.get(
                      getEhrPatientIdForItem(item),
                    );
                    return (
                      <React.Fragment key={item.id}>
                        {renderPatientRow({
                          item,
                          sectionKey: 'search',
                          indexInSection: idx,
                          apptStatus,
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </VStack>
            )}
          </Box>
        </ModalBody>

        <Divider />
        <ModalFooter
          px={{base: 4, md: 6}}
          py={{base: 4, md: 3}}
          gap={2}
          flexDirection={{
            base: deferSubmit ? 'column-reverse' : 'row',
            md: 'row',
          }}
          alignItems="stretch"
          // Respect the iOS bottom safe area on phones (notch/home
          // indicator). No-op on desktop.
          pb={{base: 'max(env(safe-area-inset-bottom), 16px)', md: 3}}
        >
          <Button
            variant="ghost"
            onClick={onClose}
            borderRadius="lg"
            mr={deferSubmit ? 0 : {base: 0, md: 2}}
            // Full-width on phones so the dismiss target is unmissable
            // and meets the 44pt touch-target minimum without hunting.
            w={{base: '100%', md: 'auto'}}
            h={{base: '48px', md: '40px'}}
            fontSize={{base: 'md', md: 'sm'}}
            fontWeight="normal"
          >
            Cancel
          </Button>
          {deferSubmit ? (
            <Button
              variant="opalitePrimary"
              onClick={handleSubmitPendingPatient}
              isDisabled={!pendingPatientItem}
              isLoading={Boolean(savingPatientId)}
              loadingText="Saving"
              borderRadius="lg"
              w={{base: '100%', md: 'auto'}}
              h={{base: '48px', md: '40px'}}
              fontSize={{base: 'md', md: 'sm'}}
              fontWeight="normal"
            >
              {submitLabel}
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeSessionPatientModal;
